import React, { useState, useEffect } from 'react';
import {
  Container,
  TextField,
  Button,
  Typography,
  Box,
  CircularProgress,
  Paper,
  Alert,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { signInWithEmailAndPassword, sendPasswordResetEmail, getIdTokenResult } from 'firebase/auth';
import { doc, setDoc, getDoc, getDocFromServer } from 'firebase/firestore';
import { auth, db } from '../firebaseConfig';
const FORMER_EMPLOYEE_LOGIN_MESSAGE = 'החשבון נעול ואינו פעיל. לבירור פנה למנהל המערכת.';
const GET_EMAIL_BY_PHONE_URL =
  process.env.REACT_APP_GET_EMAIL_BY_PHONE_URL ||
  'https://us-central1-my-repair-lab.cloudfunctions.net/getEmailByPhoneForLoginHttp';

const Login: React.FC = () => {
  const [emailOrPhone, setEmailOrPhone] = useState('');
  const [password, setPassword] = useState('');
  const [resetEmail, setResetEmail] = useState('');
  const [resetRequested, setResetRequested] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    try {
      if (sessionStorage.getItem('loginInactiveExternalUser') === '1') {
        sessionStorage.removeItem('loginInactiveExternalUser');
        setError('חשבון המשתמש החיצוני אינו פעיל. פנה למנהל המערכת.');
      }
      if (sessionStorage.getItem('loginFormerEmployee') === '1') {
        sessionStorage.removeItem('loginFormerEmployee');
        setError(FORMER_EMPLOYEE_LOGIN_MESSAGE);
      }
    } catch {
      /* ignore */
    }
  }, []);

  // Check if input is email or phone
  const isEmail = (input: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(input);
  };

  // Resolve employee email by phone via HTTP Cloud Function (CORS-enabled for localhost + production)
  const findUserByPhone = async (phone: string): Promise<string | null> => {
    try {
      const res = await fetch(GET_EMAIL_BY_PHONE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: phone.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data?.ok && data?.email) return data.email;
      return null;
    } catch (err: any) {
      console.error('Error resolving email by phone:', err);
      return null;
    }
  };

const handleLogin = async () => {
  try {
    setLoading(true);
    setError(null);

    // Determine if input is email or phone and get the email
    let userEmail = emailOrPhone.trim();
    
    // If it's not an email, try to find user by phone
    if (!isEmail(userEmail)) {
      const emailFromPhone = await findUserByPhone(userEmail);
      if (!emailFromPhone) {
        setError('משתמש עם מספר טלפון זה לא נמצא במערכת');
        setLoading(false);
        return;
      }
      userEmail = emailFromPhone;
    }

    const userCredential = await signInWithEmailAndPassword(auth, userEmail, password);

    // Force refresh token to ensure we have latest claims
    await userCredential.user.getIdToken(true);

    // Get custom claims from ID token
    const tokenResult = await getIdTokenResult(userCredential.user, true);
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.debug('[Login] role claim:', tokenResult.claims.role);
    }

    const userRole = tokenResult.claims.role;
    const externalSnap = await getDoc(doc(db, 'externalUsers', userCredential.user.uid));
    const isExternalViewer = userRole === 'external_viewer' || externalSnap.exists();

    if (isExternalViewer) {
      try {
        await setDoc(
          doc(db, 'externalUsers', userCredential.user.uid),
          { lastLogin: new Date().toISOString() },
          { merge: true },
        );
      } catch (externalLoginWriteError) {
        console.warn('External user lastLogin update skipped:', externalLoginWriteError);
      }
    } else {
      const userDocRef = doc(db, 'users', userCredential.user.uid);

      try {
        if (!userRole) {
          console.warn('User does not have a defined role in token');
          // מול השרת — "לא קיים" מהמטמון המקומי היה דורס עובד קיים לצופה
          const userDocSnap = await getDocFromServer(userDocRef);

          if (!userDocSnap.exists()) {
            await setDoc(userDocRef, {
              email: userCredential.user.email,
              displayName: userCredential.user.displayName || '',
              role: 'viewer',
              createdAt: new Date(),
              lastLogin: new Date(),
            });
          } else {
            await setDoc(userDocRef, {
              lastLogin: new Date(),
            }, { merge: true });
          }
        } else {
          await setDoc(userDocRef, {
            lastLogin: new Date(),
          }, { merge: true });
        }
      } catch (error) {
        console.error('Error accessing/updating Firestore user doc:', error);
      }
    }

    // Wait for AuthContext to finish loading the role
    // The onAuthStateChanged in AuthContext will handle setting the role
    // We'll wait a short time to ensure the auth state change is processed
    // Then navigate - PrivateRoute will handle showing loading if needed
    await new Promise(resolve => setTimeout(resolve, 500));
    
    navigate('/');
  } catch (error: any) {
    console.error('Login error:', error);
    if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password') {
      setError('שם משתמש או סיסמה שגויים');
    } else if (error.code === 'auth/too-many-requests') {
      setError('יותר מדי ניסיונות כניסה. נסה שוב מאוחר יותר');
    } else if (error.code === 'auth/user-disabled') {
      setError(FORMER_EMPLOYEE_LOGIN_MESSAGE);
    } else {
      setError(`שגיאת התחברות: ${error.message}`);
    }
  } finally {
    setLoading(false);
  }
};


  const handlePasswordReset = async () => {
    if (resetEmail) {
      try {
        setLoading(true);
        setError(null);
        await sendPasswordResetEmail(auth, resetEmail);
        alert('קישור איפוס סיסמה נשלח לאימייל שלך');
        setResetRequested(false); // Return to login screen after sending
      } catch (error: any) {
        console.error('Password reset error:', error);
        setError(`שגיאה באיפוס סיסמה: ${error.message}`);
      } finally {
        setLoading(false);
      }
    } else {
      setError('נא להזין אימייל');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      if (resetRequested) {
        handlePasswordReset();
      } else {
        handleLogin();
      }
    }
  };

  return (
    <Container maxWidth="xs">
      <Paper elevation={3} sx={{ mt: 8, p: 3, borderRadius: 2 }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            מערכת ניהול
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!resetRequested ? (
          <>
            <Typography variant="h5" gutterBottom>התחברות</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              אימייל וסיסמה או טלפון וסיסמה – באותה צורה
            </Typography>
            <TextField
              fullWidth
              label="אימייל או טלפון"
              margin="normal"
              value={emailOrPhone}
              onChange={e => setEmailOrPhone(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={loading}
              helperText="הזן אימייל או מספר טלפון (עובד או משתמש חיצוני)"
              placeholder="דוגמה: 0501234567 או your@email.com"
            />
            <TextField
              fullWidth
              label="סיסמה"
              type="password"
              margin="normal"
              value={password}
              onChange={e => setPassword(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={loading}
            />
            <Button
              variant="contained"
              fullWidth
              sx={{ mt: 2 }}
              onClick={handleLogin}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'התחבר'}
            </Button>
            <Button
              color="secondary"
              fullWidth
              sx={{ mt: 2 }}
              onClick={() => setResetRequested(true)}
              disabled={loading}
            >
              שכחתי סיסמה
            </Button>
            <Button
              color="primary"
              fullWidth
              sx={{ mt: 1 }}
              onClick={() => navigate('/track')}
              disabled={loading}
            >
              מעקב תיקון לפי מספר קריאה
            </Button>
          </>
        ) : (
          <>
            <Typography variant="h5" gutterBottom>איפוס סיסמה</Typography>
            <TextField
              fullWidth
              label="הכנס אימייל לאיפוס סיסמה"
              margin="normal"
              value={resetEmail}
              onChange={e => setResetEmail(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={loading}
            />
            <Button
              variant="contained"
              fullWidth
              sx={{ mt: 2 }}
              onClick={handlePasswordReset}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'שלח קישור לאיפוס סיסמה'}
            </Button>
            <Button
              color="secondary"
              fullWidth
              sx={{ mt: 2 }}
              onClick={() => setResetRequested(false)}
              disabled={loading}
            >
              חזור להתחברות
            </Button>
          </>
        )}
      </Paper>
    </Container>
  );
};

export default Login;